<script setup lang="ts">
import type { MemoryEvent } from '@/api/memory/memoryType';
import { Icon } from '@iconify/vue';
import { computed } from 'vue';
import { presentMemoryEvent } from './memoryEventPresentation';

const props = defineProps<{
  event: MemoryEvent;
}>();

const eventLabel = computed(() => props.event.event_type === 'chat_message' ? '聊天消息' : '平台活动');
const sourceLabel = computed(() => props.event.source === 'chat' ? 'AI 对话' : '学习活动');
const presentation = computed(() => presentMemoryEvent(props.event));

const statusClass = computed(() => {
  const code = props.event.status_code;
  if (code === null)
    return 'text-muted-foreground';
  if (code >= 200 && code < 300)
    return 'text-emerald-600 dark:text-emerald-400';
  if (code >= 400)
    return 'text-destructive';
  return 'text-muted-foreground';
});

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(value));
}
</script>

<template>
  <article class="border-b py-4 last:border-b-0">
    <div class="flex items-start gap-3">
      <div class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon :icon="event.event_type === 'chat_message' ? 'lucide:message-square' : 'lucide:activity'" class="size-4" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-sm font-medium">{{ eventLabel }}</span>
          <span class="text-xs text-muted-foreground">{{ sourceLabel }}</span>
          <span v-if="event.method" class="font-mono text-xs text-muted-foreground">{{ event.method }}</span>
          <span v-if="event.status_code !== null" class="font-mono text-xs" :class="statusClass">{{ event.status_code }}</span>
          <time class="ml-auto text-xs text-muted-foreground">{{ formatDateTime(event.created_at) }}</time>
        </div>
        <p class="mt-1 text-xs text-muted-foreground">
          {{ presentation.kind === 'chat' ? presentation.context : '答题记录' }}
        </p>

        <div v-if="presentation.kind === 'chat'" class="mt-3 grid gap-3">
          <div v-if="presentation.question" class="rounded-md border bg-muted/40 px-3 py-2.5">
            <p class="mb-1 text-xs font-medium text-muted-foreground">
              你的提问
            </p>
            <p class="whitespace-pre-wrap text-sm leading-6">
              {{ presentation.question }}
            </p>
          </div>
          <div v-if="presentation.answer" class="rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5">
            <p class="mb-1 text-xs font-medium text-primary">
              AI 回复
            </p>
            <p class="whitespace-pre-wrap text-sm leading-6">
              {{ presentation.answer }}
            </p>
          </div>
        </div>

        <div v-else class="mt-3 grid gap-3 rounded-md border bg-muted/30 px-3 py-3">
          <div class="flex flex-wrap items-center gap-2">
            <span class="rounded-full bg-background px-2 py-0.5 text-xs text-muted-foreground">{{ presentation.questionType }}</span>
            <span
              class="rounded-full px-2 py-0.5 text-xs font-medium"
              :class="{
                'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300': presentation.result.tone === 'success',
                'bg-amber-500/10 text-amber-700 dark:text-amber-300': presentation.result.tone === 'pending',
                'bg-destructive/10 text-destructive': presentation.result.tone === 'danger',
                'bg-background text-muted-foreground': presentation.result.tone === 'neutral',
              }"
            >
              {{ presentation.result.label }}
            </span>
          </div>
          <div v-if="presentation.question">
            <p class="mb-1 text-xs font-medium text-muted-foreground">
              题目
            </p>
            <p class="whitespace-pre-wrap text-sm leading-6">
              {{ presentation.question }}
            </p>
          </div>
          <div v-if="presentation.answer" class="border-t pt-3">
            <p class="mb-1 text-xs font-medium text-muted-foreground">
              你的作答
            </p>
            <p class="whitespace-pre-wrap text-sm leading-6">
              {{ presentation.answer }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
